import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import { TopBarRedesigned } from 'components/TopBarRedesigned';
import { MenuItem } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FormMaterialSelector } from "components/Selector";
import Api from 'api';
import useActionApi from 'hooks/useActionApi';
import FixedLoader from 'components/FixedLoader';
import { exitReact, getPaymentTerms } from 'utils';
import { timePickerOptions } from '../../dentist/PostJobPage/PostJob.utils';
import { Button } from '@mui/material';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import { SimpleModal } from 'components/Modal';
import { CrossButtonBlack } from 'components/Buttons/CrossButton';
import {
    TitleContainer,
    PopupTitle,
    ModalText,
} from '../ContactSupport/ContactSupport.styles';

import {
    PageContainer,
    TimeInputField,
    BillingRow,
    SectionContainer,
    InlineEditContainer,
    CompactFormControl,
    HeaderContainer,
    GHRNotificationRow,
} from './Invoice.styles';

import { Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import MaterialInput from "components/Inputs/MaterialInput";
import { Switch, FormControlLabel } from '@mui/material';
import { Show } from 'components/Conditionals';

interface AutocompleteProps {
    buildingNumber?: string
    state: string,
    city?: string,
    postalCode?: string,
    address?: string,
    valueEqual?: boolean
}

interface LocationState {
    job: any;
    isViewInvoice?: boolean;
}

const HygienistInvoicePage: React.FC = () => {
    const history = useHistory();
    const { job: jobData, isViewInvoice = false } = history.location.state as LocationState;
    const [isEdit, setIsEdit] = useState(false);

    const parseTimeString = useCallback((timeStr: string) => {
        const normalizedTimeStr = timeStr.toLowerCase().trim();

        if (normalizedTimeStr.includes('gmt')) {
            return new Date(timeStr);
        }

        const [time, period] = normalizedTimeStr.split(/\s+/);
        let [hours, minutes] = time.split(':').map(Number);

        if (period === 'pm' && hours !== 12) {
            hours += 12;
        } else if (period === 'am' && hours === 12) {
            hours = 0;
        }

        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date;
    }, []);

    const generateTimeOptions = useCallback(() => {
        const postedStartDate = parseTimeString(jobData.shifts[0].postedStart);
        const oneHourBefore = new Date(postedStartDate);
        oneHourBefore.setHours(oneHourBefore.getHours() - 1);
        
        const startHour = oneHourBefore.getHours();
        const endHour = 23;
        const step = 15;
        
        let timeArray = [];
        const baseDate = new Date();
        
        for (let hour = startHour; hour <= endHour; hour++) {
            for (let minute = 0; minute < 60; minute += step) {
                const currentDate = new Date(baseDate);
                currentDate.setHours(hour, minute, 0, 0);
                timeArray.push(currentDate);
            }
        }
        
        return timeArray.map((time, index) => (
            <MenuItem key={index} value={time.toString()}>
                {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
            </MenuItem>
        ));
    }, [jobData.shifts, parseTimeString]);

    const timeOptions = useMemo(() => generateTimeOptions(), [generateTimeOptions]);

    const formatDatabaseTimeForSelector = useCallback((dbTime: string) => {
        if (!dbTime || !timeOptions) return '';

        try {
            const [time, period] = dbTime.toLowerCase().trim().split(/\s+/);
            const [hours, minutes] = time.split(':').map(Number);
            const targetTimeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period.toUpperCase()}`;

            const matchingOption = timeOptions.find(opt => opt.props.children === targetTimeString);
            if (matchingOption) {
                return matchingOption.props.value;
            }
            return '';
        } catch (error) {
            console.error('Error formatting time for selector:', error);
            return '';
        }
    }, [timeOptions]);

    const defaultFormValues = useMemo(() => ({
        startTime: formatDatabaseTimeForSelector((isViewInvoice || isEdit) ? jobData.shifts[0].actualStart : jobData.shifts[0].postedStart),
        endTime: formatDatabaseTimeForSelector((isViewInvoice || isEdit) ? jobData.shifts[0].actualEnd : jobData.shifts[0].postedEnd),
        unpaidTime: jobData.shifts[0].unpaidHours ? (jobData.shifts[0].unpaidHours).toString() : '0'
    }), [formatDatabaseTimeForSelector, isViewInvoice, isEdit, jobData.shifts]);

    const {
        control,
        handleSubmit,
        watch,
        setValue
    } = useForm({
        defaultValues: defaultFormValues
    });

    const startTime = useWatch({ control, name: 'startTime' });
    const endTime = useWatch({ control, name: 'endTime' });
    const unpaidTime = useWatch({ control, name: 'unpaidTime' });

    const [totalHours, setTotalHours] = useState<number>(0);
    const [billableHours, setBillableHours] = useState<number>(0);
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const [travelAllowance, setTravelAllowance] = useState<number>(0);
    const [showTimeWarningPopup, setShowTimeWarningPopup] = useState(false);
    const [showStripeModal, setShowStripeModal] = useState(false);
    const [isPostInvoiceStripeModal, setIsPostInvoiceStripeModal] = useState(false);
    const [hasConfirmedEarlyTime, setHasConfirmedEarlyTime] = useState(false);
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [isEditingETransfer, setIsEditingETransfer] = useState(false);
    const [editedAddress, setEditedAddress] = useState(jobData.hygienist.address);
    const [editedETransfer, setEditedETransfer] = useState(jobData.hygienist.etransferEmail);
    const [tempETransfer, setTempETransfer] = useState(jobData.hygienist.etransferEmail);
    const [autocompletedAddress, setAutocompletedAddress] = useState<AutocompleteProps | undefined>();
    const [addressError, setAddressError] = useState<string>('');
    const [isPaid, setIsPaid] = useState(jobData.invoice?.hygienistMarkedPaid || false);
    const [distance, setDistance] = useState<number | null>(null);

    const isEndTimeValid = useCallback((endTimeStr: string, startTimeStr: string) => {
        if (!startTimeStr || !endTimeStr) return true;
        const startDate = parseTimeString(startTimeStr);
        const endDate = parseTimeString(endTimeStr);
        return endDate > startDate;
    }, [parseTimeString]);

    const formatDisplayHours = useCallback((value: number) => {
        const hours = Math.floor(value);
        const minutes = Math.round((value - hours) * 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }, []);

    const formatTimeDisplay = useCallback((timeStr: string) => {
        if (!timeStr) return '';

        if (timeStr.match(/^\d{1,2}:\d{2}\s?(?:AM|PM|am|pm)$/)) {
            return timeStr;
        }

        try {
            const date = new Date(timeStr);
            return date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            return timeStr;
        }
    }, []);

    const formatTimeForDatabase = useCallback((timeStr: string) => {
        if (!timeStr) return '';

        try {
            if (timeStr.match(/^\d{1,2}:\d{2}\s?(?:AM|PM|am|pm)$/)) {
                const [time, period] = timeStr.toLowerCase().trim().split(/\s+/);
                const [hours, minutes] = time.split(':').map(Number);
                const hour12 = period === 'pm' && hours !== 12 ? hours % 12 : hours === 12 && period === 'am' ? 0 : hours;
                return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
            }

            const date = new Date(timeStr);
            if (!isNaN(date.getTime())) {
                const hours = date.getHours();
                const minutes = date.getMinutes();
                const period = hours >= 12 ? 'pm' : 'am';
                const hour12 = hours % 12 || 12;
                return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
            }

            return '';
        } catch (error) {
            console.error('Error formatting time for database:', error);
            return '';
        }
    }, []);

    useEffect(() => {
        if (isEditingAddress) {
            // @ts-ignore
            const TempStars: any = window.TempStars;
            TempStars.Autocomplete.init(
                { country: jobData.hygienist.country },
                ({ state, city, postalCode, address, buildingNumber, valueEqual }: AutocompleteProps) => {
                    setAutocompletedAddress({ state, city, postalCode, address, buildingNumber, valueEqual });
                }
            );

            return () => TempStars.Autocomplete.removeListener();
        }
    }, [isEditingAddress, jobData.hygienist.country]);

    const compareTimeStrings = useCallback((time1: string, time2: string) => {
        const date1 = new Date(time1);
        const hours1 = date1.getHours();
        const minutes1 = date1.getMinutes();

        const [timeStr, period] = time2.toLowerCase().split(' ');
        let [hours2, minutes2] = timeStr.split(':').map(Number);

        if (period === 'pm' && hours2 !== 12) {
            hours2 += 12;
        } else if (period === 'am' && hours2 === 12) {
            hours2 = 0;
        }

        if (hours1 !== hours2) {
            return hours1 - hours2;
        }
        return minutes1 - minutes2;
    }, []);

    const calculateTravelAllowance = useCallback((distanceInKm: number): number => {
        if (distanceInKm <= 20) {
            return 0;
        } else if (distanceInKm < 46) {
            return 7.50;
        } else if (distanceInKm < 76) {
            return 15.00;
        } else if (distanceInKm < 91) {
            return 25.00;
        } else {
            return 30.00;
        }
    }, []);

    const [getDistanceMatrixLoading, , getDistanceMatrix] = useActionApi({
        apiMethod: Api.getDistanceMatrix,
        onSuccess: (data: any) => {
            const distanceInKm = (data as { distance: { value: number } }).distance.value / 1000;
            setDistance(distanceInKm);
        }
    });

    useEffect(() => {
        const fetchDistance = async () => {
            if (jobData.Autobook === 1 && jobData.location?.lat && jobData.location?.lon) {
                try {
                    await getDistanceMatrix({
                        lat: jobData.location.lat,
                        lng: jobData.location.lon
                    });
                } catch (error) {
                    console.error('Error calculating distance:', error);
                    setDistance(null);
                }
            }
        };

        fetchDistance();
    }, [jobData.Autobook, jobData.location]);

    useEffect(() => {
        if (jobData.Autobook === 1 && distance !== null) {
            const allowance = calculateTravelAllowance(distance);
            setTravelAllowance(allowance);
        } else {
            setTravelAllowance(0);
        }
    }, [distance, jobData.Autobook]);

    useEffect(() => {
        if (startTime && endTime) {
            if (!isEndTimeValid(endTime, startTime)) {
                setValue('endTime', '');
                return;
            }

            try {
                const startDate = parseTimeString(startTime);
                const endDate = parseTimeString(endTime);

                if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                    throw new Error("Invalid date parsing");
                }

                const diffMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
                const totalHoursDecimal = diffMinutes / 60;
                setTotalHours(totalHoursDecimal);

                const unpaidHours = parseFloat(unpaidTime) || 0;
                const billableHoursDecimal = Math.max(0, totalHoursDecimal - unpaidHours);
                setBillableHours(billableHoursDecimal);

                const billableAmount = billableHoursDecimal * jobData.hourlyRate;
                setTotalAmount(billableAmount + travelAllowance);
            } catch (error) {
                console.error("Time calculation error:", error);
                setTotalHours(0);
                setBillableHours(0);
                setTotalAmount(0);
            }
        }
    }, [startTime, endTime, unpaidTime, travelAllowance, jobData.hourlyRate, parseTimeString, isEndTimeValid, setValue]);

    const handleCreateStripeModal = useCallback(() => {
        setIsPostInvoiceStripeModal(false);
        setShowStripeModal(true);
    }, []);

    const onInvoiceSuccess = useCallback(() => {
        if (!jobData.stripeActiveStatus) {
            setIsPostInvoiceStripeModal(true);
            setShowStripeModal(true);
        } else {
            history.push('loading');
            exitReact('home');
        }
    }, [jobData.stripeActiveStatus, history]);

    const [createInvoiceLoading, , createInvoice] = useActionApi({
        apiMethod: Api.sendInvoice,
        onSuccess: onInvoiceSuccess
    });

    const [resendInvoiceLoading, , resendInvoice] = useActionApi({
        apiMethod: Api.resendInvoice,
        onSuccess: onInvoiceSuccess
    });

    const [updateInvoiceLoading, , updateInvoice] = useActionApi({
        apiMethod: Api.updateInvoice,
        onSuccess: () => {
        }
    });

    const prepareResendInvoiceData = useCallback(async () => {
        const useAdjustedTimes = startTime && endTime;

        let calculatedData;
        if (useAdjustedTimes) {
            calculatedData = {
                actualStart: formatTimeForDatabase(startTime),
                actualEnd: formatTimeForDatabase(endTime),
                totalHours,
                unpaidHours: unpaidTime ? unpaidTime : jobData.shifts[0].unpaidHours,
                billableHours,
                travelAllowance,
                totalAmt: totalAmount,
            };
        } else {
            calculatedData = {
                actualStart: jobData.shifts[0].actualStart,
                actualEnd: jobData.shifts[0].actualEnd,
                totalHours: jobData.invoice.totalHours,
                unpaidHours: jobData.invoice.totalUnpaidHours,
                billableHours: jobData.invoice.totalBillableHours,
                travelAllowance,
                totalAmt: jobData.invoice.totalInvoiceAmt,
            };
        }

        const data = {
            jobId: jobData.id,
            actualStart: calculatedData.actualStart,
            actualEnd: calculatedData.actualEnd,
            totalHours: calculatedData.totalHours,
            unpaidHours: calculatedData.unpaidHours,
            billableHours: calculatedData.billableHours,
            totalAmt: calculatedData.totalAmt,
            hourlyRate: jobData.invoice.hourlyRate,
            travelAllowance: calculatedData.travelAllowance,
            sentOn: dayjs(jobData.invoice.sentOn).format('MMM D, YYYY'),
            id: jobData.invoice.id,
        };

        return data;
    }, [startTime, endTime, totalHours, unpaidTime, jobData, billableHours, travelAllowance, totalAmount, formatTimeForDatabase]);

    const handleCreateInvoice = useCallback(async () => {
        if (!startTime || !endTime) return;

        try {
            if (isViewInvoice) {
                const data = await prepareResendInvoiceData();
                await resendInvoice(data);
                history.push('loading');
                exitReact('home');
            } else {
                const invoiceData = {
                    jobId: jobData.id,
                    actualStart: formatTimeForDatabase(startTime),
                    actualEnd: formatTimeForDatabase(endTime),
                    unpaidHours: unpaidTime,
                    travelAllowance,
                    totalHours,
                    billableHours,
                    totalAmt: totalAmount,
                    hourlyRate: jobData.hourlyRate
                };

                await createInvoice(invoiceData);
            }
        } catch (error) {
            console.error('Error handling invoice:', error);
        }
    }, [startTime, endTime, isViewInvoice, prepareResendInvoiceData, resendInvoice, history, jobData.id, formatTimeForDatabase, unpaidTime, travelAllowance, totalHours, billableHours, totalAmount, jobData.hourlyRate, createInvoice]);

    const checkStartTimeChange = useCallback((newStartTime: string) => {
        const selectedTime = newStartTime;
        const postedStart = jobData.shifts[0].postedStart;

        const selectedDate = parseTimeString(selectedTime);
        const postedStartDate = parseTimeString(postedStart);
        const oneHourBefore = new Date(postedStartDate);
        oneHourBefore.setHours(oneHourBefore.getHours() - 1);

        if (selectedDate < oneHourBefore) {
            setValue('startTime', oneHourBefore.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            }));
            return;
        }

        if (!hasConfirmedEarlyTime && compareTimeStrings(selectedTime, postedStart) < 0) {
            setShowTimeWarningPopup(true);
            setValue('startTime', selectedTime);
            return;
        }
        setValue('startTime', selectedTime);
    }, [jobData.shifts, parseTimeString, hasConfirmedEarlyTime, compareTimeStrings, setValue]);

    const handleAddressUpdate = useCallback(async () => {
        if (!autocompletedAddress?.valueEqual) {
            setAddressError('Please select your address from the list');
            return;
        }
        if (!autocompletedAddress?.buildingNumber?.length) {
            setAddressError('Add your building #, street');
            return;
        }

        try {
            await Api.updateHygienistAccount({
                address: autocompletedAddress.address,
                city: autocompletedAddress.city,
                province: autocompletedAddress.state,
                postalCode: autocompletedAddress.postalCode
            });
            setEditedAddress(autocompletedAddress.address || '');
            setIsEditingAddress(false);
            setAddressError('');
        } catch (error) {
            console.error('Error updating address:', error);
            setAddressError('Failed to update address');
        }
    }, [autocompletedAddress]);

    const handleETransferUpdate = useCallback(async () => {
        try {
            await Api.updateHygienistAccount({
                etransferEmail: tempETransfer
            });
            setEditedETransfer(tempETransfer);
            setIsEditingETransfer(false);
        } catch (error) {
            console.error('Error updating e-transfer:', error);
        }
    }, [tempETransfer]);

    const handlePaidToggle = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        try {
            await updateInvoice({ id: jobData.invoice.id, hygienistMarkedPaid: isChecked ? 1 : 0 });
            setIsPaid(isChecked);
        } catch (error) {
            console.error('Error updating paid status:', error);
            setIsPaid(!isChecked);
        }
    }, [jobData.invoice, updateInvoice]);

    useEffect(() => {
        const startTimeValue = formatDatabaseTimeForSelector((isViewInvoice || isEdit) ? jobData.shifts[0].actualStart : jobData.shifts[0].postedStart);
        const endTimeValue = formatDatabaseTimeForSelector((isViewInvoice || isEdit) ? jobData.shifts[0].actualEnd : jobData.shifts[0].postedEnd);

        if (startTimeValue) setValue('startTime', startTimeValue);
        if (endTimeValue) setValue('endTime', endTimeValue);
    }, [isViewInvoice, isEdit, jobData.shifts, formatDatabaseTimeForSelector, setValue]);

    const handleBack = useCallback(() => {
        history.push('loading');
        exitReact('home');
    }, [history]);

    // Memoize the time selector components to prevent unnecessary re-renders
    const startTimeSelector = useMemo(() => (
        <TimeInputField>
            <FormMaterialSelector
                control={control}
                name="startTime"
                label="Arrival Time"
                disabled={isViewInvoice && !isEdit}
            >
                {[
                    <MenuItem key="default" value="">Select Start Time</MenuItem>,
                    ...timeOptions.map(opt => (
                        <MenuItem
                            key={opt.props.value}
                            value={opt.props.value}
                            onClick={() => { checkStartTimeChange(opt.props.value) }}
                        >
                            {opt.props.children}
                        </MenuItem>
                    ))
                ]}
            </FormMaterialSelector>
        </TimeInputField>
    ), [control, isViewInvoice, isEdit, timeOptions, checkStartTimeChange]);

    const endTimeSelector = useMemo(() => (
        <TimeInputField>
            <FormMaterialSelector
                control={control}
                name="endTime"
                label="End Time"
                disabled={isViewInvoice && !isEdit}
            >
                {[
                    <MenuItem key="default" value="">Select End Time</MenuItem>,
                    ...timeOptions
                ]}
            </FormMaterialSelector>
        </TimeInputField>
    ), [control, isViewInvoice, isEdit, timeOptions]);

    const unpaidTimeSelector = useMemo(() => (
        <TimeInputField>
            <FormMaterialSelector
                control={control}
                name="unpaidTime"
                label="Unpaid Time"
                disabled={isViewInvoice && !isEdit}
            >
                {[
                    { value: 0, label: "0 mins" },
                    { value: 0.25, label: "15 mins" },
                    { value: 0.5, label: "30 mins" },
                    { value: 0.75, label: "45 mins" },
                    { value: 1, label: "1 hour" },
                    { value: 1.25, label: "1.15 hrs" },
                    { value: 1.5, label: "1.30 hrs" },
                    { value: 1.75, label: "1.45 hrs" },
                    { value: 2, label: "2 hrs" },
                ].map(option =>
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                )}
            </FormMaterialSelector>
        </TimeInputField>
    ), [control, isViewInvoice, isEdit]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <>
                <TopBarRedesigned
                    title={isViewInvoice ? "Invoice Detail" : "Create Invoice"}
                    onBack={handleBack}
                />

                <PageContainer>
                    <SectionContainer>
                        <HeaderContainer>
                            <div>
                                <h2>{jobData.location.practiceName}</h2>
                                <div>{jobData.location.address}</div>
                                <div>{jobData.location.city}, {jobData.location.province}</div>
                                <div>{jobData.location.postalCode}</div>
                            </div>
                            <div className="invoice-number">
                                Invoice #{jobData.id}
                            </div>
                        </HeaderContainer>
                    </SectionContainer>

                    <SectionContainer>
                        <h2>Completed Shift Details</h2>
                        <div>Start Time: {formatTimeDisplay(jobData.shifts[0].postedStart)}</div>
                        <div>End Time: {formatTimeDisplay(jobData.shifts[0].postedEnd)}</div>
                    </SectionContainer>

                    <SectionContainer>
                        <h2>Actual Shift Details</h2>
                        {startTimeSelector}
                        {endTimeSelector}
                        {unpaidTimeSelector}
                    </SectionContainer>

                    <SectionContainer>
                        <h2>Totals</h2>
                        <BillingRow>
                            <div>Total Time</div>
                            <div>{formatDisplayHours(totalHours)} hrs</div>
                        </BillingRow>
                        <BillingRow>
                            <div>Billable Time</div>
                            <div>{formatDisplayHours(billableHours)} hrs</div>
                        </BillingRow>
                        <BillingRow>
                            <div>Hourly Rate</div>
                            <div>${jobData.hourlyRate}/hr</div>
                        </BillingRow>
                        <Show if={!!(jobData?.ghr)}>
                            <GHRNotificationRow>
                                <div><span>TempStars</span> will compensate you an additional<span className="amount">${jobData.ghr - jobData.hourlyRate}/hr</span> to meet your <span>Guaranteed Hourly Rate</span> of <span>${jobData.ghr}/hr</span></div>
                            </GHRNotificationRow>
                        </Show>
                        <BillingRow>
                            <div>Travel Allowance</div>
                            <div>${travelAllowance.toFixed(2)}</div>
                        </BillingRow>
                        <BillingRow isTotalAmount>
                            <div className="total-amount">Total:</div>
                            <div className="total-amount">${totalAmount.toFixed(2)}</div>
                        </BillingRow>
                    </SectionContainer>

                    <SectionContainer isPaymentDetails>
                        <h2>Payment Details</h2>
                        <BillingRow>
                            <div>Your Mailing Address:</div>
                            <InlineEditContainer>
                                {isEditingAddress ? (
                                    <>
                                        <CompactFormControl>
                                            <MaterialInput
                                                className="no-fastclick"
                                                id="autocomplete"
                                                name="address"
                                                type="text"
                                                error={!!addressError}
                                                helperText={addressError}
                                            />
                                        </CompactFormControl>
                                        {(!isViewInvoice || isEdit) && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleAddressUpdate}
                                                    className="no-fastclick"
                                                >
                                                    <SaveIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setIsEditingAddress(false);
                                                        setAddressError('');
                                                    }}
                                                    className="no-fastclick"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div>{editedAddress}</div>
                                        {(!isViewInvoice || isEdit) && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setIsEditingAddress(true)}
                                                    className="no-fastclick"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                )}
                            </InlineEditContainer>
                        </BillingRow>
                        <BillingRow>
                            <div>Your E-Transfer Address:</div>
                            <InlineEditContainer>
                                {isEditingETransfer ? (
                                    <>
                                        <CompactFormControl>
                                            <MaterialInput
                                                className="no-fastclick"
                                                name="etransfer"
                                                value={tempETransfer}
                                                onChange={(e) => setTempETransfer(e.target.value)}
                                                type="text"
                                            />
                                        </CompactFormControl>
                                        {(!isViewInvoice || isEdit) && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleETransferUpdate}
                                                    className="no-fastclick"
                                                >
                                                    <SaveIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setIsEditingETransfer(false);
                                                        setTempETransfer(editedETransfer);
                                                    }}
                                                    className="no-fastclick"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div>{editedETransfer}</div>
                                        {(!isViewInvoice || isEdit) && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setIsEditingETransfer(true)}
                                                    className="no-fastclick"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </>
                                )}
                            </InlineEditContainer>
                        </BillingRow>
                        <BillingRow>
                            <div>Stripe Express Payments:</div>
                            {jobData.stripeActiveStatus ? (
                                <div className="stripe-active">Active</div>
                            ) : (
                                <Button
                                    variant="text"
                                    className="stripe-inactive no-fastclick"
                                    onClick={handleCreateStripeModal}
                                    sx={{
                                        padding: 0,
                                        textTransform: 'none',
                                        '&:hover': {
                                            background: 'none'
                                        }
                                    }}
                                >
                                    Get paid faster, set up Stripe Express here
                                </Button>
                            )}
                        </BillingRow>
                        <BillingRow>
                            <div>Payment Terms:</div>
                            <div>
                                {getPaymentTerms(jobData.paymentTerms)}
                            </div>
                        </BillingRow>
                        <BillingRow>
                            <div>Expect Payment By:</div>
                            <div>{dayjs(jobData.invoice.sentOn ? jobData.invoice.sentOn : dayjs()).add(jobData.paymentTerms, 'day').format('YYYY-MM-DD')}</div>
                        </BillingRow>
                        {isViewInvoice && (
                            <BillingRow>
                                <div>Payment Status:</div>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isPaid}
                                            onChange={handlePaidToggle}
                                            color="primary"
                                        />
                                    }
                                    label={isPaid ? "Paid!" : "Not Paid"}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '14px',
                                            color: isPaid ? 'success.main' : 'text.primary'
                                        }
                                    }}
                                />
                            </BillingRow>
                        )}
                    </SectionContainer>

                    {!isViewInvoice ? (
                        <StyledButton
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleCreateInvoice}
                            disabled={!startTime || !endTime}
                            className="no-fastclick"
                        >
                            Create Invoice
                        </StyledButton>
                    ) : (
                        <>
                            <StyledButton
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleCreateInvoice}
                                className="no-fastclick"
                                disabled={isPaid}
                            >
                                Resend Invoice
                            </StyledButton>
                            <StyledButton
                                variant="outlined"
                                color="primary"
                                size="large"
                                onClick={() => setIsEdit(!isEdit)}
                                sx={{ mt: 2 }}
                                className="no-fastclick"
                            >
                                {isEdit ? 'Cancel' : 'Edit and Resend Invoice'}
                            </StyledButton>
                        </>
                    )}

                    <FixedLoader loading={createInvoiceLoading || resendInvoiceLoading || updateInvoiceLoading || getDistanceMatrixLoading} />

                    <SimpleModal isOpen={showTimeWarningPopup}>
                        <TitleContainer>
                            <PopupTitle>Paid Time Starts at Posted Arrival Time</PopupTitle>
                            <CrossButtonBlack onClick={() => setShowTimeWarningPopup(false)} className="no-fastclick" />
                        </TitleContainer>
                        <ModalText>
                            Paid time for this shifts starts at the Arrival Time originally posted for this shift,
                            not before - regardless of when you arrive at the office.
                            <br /><br />
                            Have you verbally confirmed with office management to invoice for time starting before
                            their posted Arrival Time?
                        </ModalText>
                        <StyledButton
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => {
                                setHasConfirmedEarlyTime(true);
                                setShowTimeWarningPopup(false);
                                setValue('startTime', startTime);
                            }}
                            className="no-fastclick"
                        >
                            Yes
                        </StyledButton>
                        <StyledButton
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => {
                                setShowTimeWarningPopup(false);
                                setValue('startTime', jobData.shifts[0].postedStart);
                            }}
                            className="no-fastclick"
                            sx={{ mt: 2 }}
                        >
                            No
                        </StyledButton>
                    </SimpleModal>

                    <SimpleModal isOpen={showStripeModal}>
                        <TitleContainer>
                            <PopupTitle>Set up Express Payment</PopupTitle>
                            <CrossButtonBlack onClick={() => setShowStripeModal(false)} className="no-fastclick" />
                        </TitleContainer>
                        <ModalText>
                            Set up Express Payment for faster payouts?
                        </ModalText>
                        <StyledButton
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => {
                                setShowStripeModal(false);
                                history.push('hygienistProfile');
                            }}
                            className="no-fastclick"
                        >
                            OK
                        </StyledButton>
                        <StyledButton
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => {
                                setShowStripeModal(false);
                                if (isPostInvoiceStripeModal) {
                                    history.push('loading');
                                    exitReact('home', { id: jobData.id, invoiceSubmitted: true });
                                }
                            }}
                            className="no-fastclick"
                            sx={{ mt: 2 }}
                        >
                            No
                        </StyledButton>

                    </SimpleModal>
                </PageContainer>
            </>
        </LocalizationProvider>
    );
};

export default HygienistInvoicePage;
